/**
 * Pestañas de Emitidos y Recibidos (spec 2026-10-11 §3.1): con qué pestaña abre la pantalla, qué columnas y qué
 * títulos lleva cada una, qué se exporta, qué enseña la barra de totales, el importe de cada fila y los textos de
 * los vacíos (window.FDPestanas). Funciones puras (Vitest en entorno node, sin DOM ni jQuery); el montaje vive en
 * application/views/noble/documentos/listado.php y listado_recibidos.php. El servidor vuelve a validar la pestaña
 * (Cfdi_lista_model::PESTANAS) y nunca confía en lo que diga esta capa.
 */
export const TIPOS = ['todos', 'I', 'E', 'T', 'P', 'N'];
export const NOMBRE = { todos: 'Todos', I: 'Ingresos', E: 'Egresos', T: 'Traslados', P: 'Pagos', N: 'Nómina' };
export const NOMBRE_TIPO = { I: 'Ingreso', E: 'Egreso', T: 'Traslado', P: 'Pago', N: 'Nómina' };
export const ICONO = { todos: 'ti-files', I: 'ti-file-invoice', E: 'ti-file-minus', T: 'ti-truck', P: 'ti-cash', N: 'ti-users' };
const VACIO = { todos: 'comprobantes', I: 'facturas', E: 'notas de crédito', T: 'traslados', P: 'pagos', N: 'recibos de nómina' };

/**
 * Orden de las columnas de cada tabla: el MISMO de sus <th> y de su arreglo `columns` (lo fijan
 * __tests__/pestanas-vista-emitidos.test.js y __tests__/pestanas-vista-recibidos.test.js leyendo la vista).
 */
export const COLUMNAS = {
  emitidos: ['sel', 'folio', 'tipo', 'fecha', 'cliente', 'moneda', 'subtotal', 'ieps', 'iva', 'ret_isr', 'ret_iva',
    'total', 'mp', 'cobranza', 'acciones'],
  recibidos: ['detalle', 'uuid', 'folio', 'tipo', 'fecha', 'proveedor', 'rfc', 'uso', 'moneda', 'subtotal', 'ieps', 'iva',
    'ret_isr', 'ret_iva', 'total', 'categoria', 'mp', 'rep', 'acciones', 'estatus'],
};
const IMPORTES = ['subtotal', 'ieps', 'iva', 'ret_isr', 'ret_iva'];
/** Lo que cada pestaña NO enseña (spec §3.1). «Tipo» solo vive en Todos; Cobranza / Recibo de pago, solo con facturas. */
const OCULTAS = {
  emitidos: {
    todos: [],
    I: ['tipo'],
    E: ['tipo', 'cobranza'],
    T: ['tipo', 'cobranza'].concat(IMPORTES),
    P: ['tipo', 'cobranza', 'moneda', 'mp'].concat(IMPORTES),
    N: ['tipo', 'cobranza'],
  },
  recibidos: {
    todos: [],
    I: ['tipo'],
    E: ['tipo', 'rep'],
    T: ['tipo', 'rep'].concat(IMPORTES),
    P: ['tipo', 'rep', 'moneda', 'mp'].concat(IMPORTES),
    N: ['tipo', 'rep'],
  },
};
/** Columnas que nunca salen en Excel / Copiar / Imprimir / PDF (casillas, acciones, el chevrón). */
const NO_EXPORTA = { emitidos: ['sel', 'acciones'], recibidos: ['detalle', 'acciones'] };
/** Títulos que cambian con la pestaña; `_` es el del <th> de la vista. */
const TITULOS = {
  emitidos: {
    cliente: { _: 'Cliente', N: 'Trabajador' },
    total: { _: 'Total', P: 'Monto del pago', N: 'Neto pagado' },
  },
  recibidos: {
    proveedor: { _: 'Proveedor', N: 'Empleador' },
    total: { _: 'Total', P: 'Monto del pago', N: 'Neto pagado' },
  },
};
/**
 * Valor de un catálogo por su clave PROPIA: `NOMBRE['constructor']` o `OCULTAS['toString']` no son del catálogo
 * (vienen de Object.prototype) y no deben pintarse ni romper nada.
 */
function propio(catalogo, clave) {
  return Object.prototype.hasOwnProperty.call(catalogo, clave) ? catalogo[clave] : undefined;
}
const ESTADISTICAS = ['registros', 'subtotal', 'descuento', 'ieps', 'iva', 'ret_isr', 'ret_iva', 'total'];
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

export function esc(texto) {
  return String(texto == null ? '' : texto)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function miles(entero) { return String(entero).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }

/**
 * Redondeo decimal a centavos, el medio centavo hacia arriba (como la factura): 1.005 → 1.01. `toFixed(2)` lo
 * hace en binario y deja 1.00; con notación exponencial se redondea sobre los dígitos que escribimos.
 */
function aCentavos(abs) {
  const r = Number(Math.round(Number(abs + 'e2')) + 'e-2');
  return Number.isFinite(r) ? r : Math.round(abs * 100) / 100;
}

/** 1234.5 → '$1,234.50'; vacío o no numérico → '—'; nunca '-$0.00'. */
export function dinero(n) {
  const v = Number(n);
  if (n === null || n === undefined || n === '' || !Number.isFinite(v)) return '—';
  const r = aCentavos(Math.abs(v));
  const partes = r.toFixed(2).split('.');
  return (v < 0 && r > 0 ? '-' : '') + '$' + miles(partes[0]) + '.' + partes[1];
}

/** '2026-10-02 13:00:00' → '2-oct-2026'; sin fecha → '—'. */
export function fechaCorta(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!m) return '—';
  const mes = parseInt(m[2], 10);
  return (mes >= 1 && mes <= 12) ? parseInt(m[3], 10) + '-' + MESES[mes - 1] + '-' + m[1] : '—';
}

export function normalizarTipo(t) { return TIPOS.indexOf(t) !== -1 ? t : null; }

/**
 * Pestaña con la que abre la pantalla. Una liga (tablero, correo, ?tipo=) manda lo que el servidor dejó en la sesión;
 * si no, lo último que recordó este navegador, luego la sesión y, la primera vez, Todos (spec §3.1).
 */
export function pestanaInicial(o) {
  const op = o || {};
  if (op.deLiga) return normalizarTipo(op.sesion) || 'todos';
  return normalizarTipo(op.local) || normalizarTipo(op.sesion) || 'todos';
}

/**
 * Pestaña con la que abre Emitidos (pulido 2026-10-12, pedido de Adrián): Ingresos, salvo que la persona se haya
 * cambiado de pestaña; entonces la última que eligió en este navegador. Precedencia:
 *   1. una liga explícita (tablero, correo, ?tipo=, «Ver en la tabla»): la pestaña que dejó el servidor en la sesión;
 *   2. la que la persona eligió a mano la última vez (`recordada`, localStorage por emisor y usuario);
 *   3. `porOmision` (Ingresos).
 * La sesión ya no decide sin liga: una liga de ayer o un «Ver en la tabla» no deben cambiar con qué abre mañana.
 */
export function pestanaAlAbrir(o) {
  const op = o || {};
  const omision = normalizarTipo(op.porOmision) || 'I';
  if (op.deLiga) return normalizarTipo(op.sesion) || omision;
  return normalizarTipo(op.recordada) || omision;
}

/** Número de una pestaña: '1,234'; sin dato → '—'. */
export function textoConteo(n) {
  const v = Number(n);
  if (n === null || n === undefined || n === '' || !Number.isFinite(v) || v < 0) return '—';
  return miles(Math.trunc(v));
}

/** Vacío amable de cada pestaña (spec §5). */
export function textoVacio(tipo) { return 'Aún no hay ' + (propio(VACIO, tipo) || VACIO.todos) + ' en este período.'; }

export function ocultasDe(pantalla, tipo) {
  const p = propio(OCULTAS, pantalla) || {};
  return (propio(p, tipo) || p.todos || []).slice();
}

export function tituloColumna(pantalla, clave, tipo) {
  const t = propio(propio(TITULOS, pantalla) || {}, clave);
  if (!t) return null;
  return propio(t, tipo) !== undefined ? t[tipo] : t._;
}

/**
 * Cada columna con su visibilidad en la pestaña: la pestaña oculta lo que no le toca y la persona oculta lo suyo
 * (el menú «Columnas», guardado por índice en el navegador). `titulo` es null si la columna no cambia de título.
 * @returns {{idx:number, clave:string, visible:boolean, titulo:(string|null)}[]}
 */
export function vistaColumnas(pantalla, tipo, ocultasUsuario) {
  const cols = propio(COLUMNAS, pantalla) || [];
  const porPestana = ocultasDe(pantalla, tipo);
  const usuario = Array.isArray(ocultasUsuario) ? ocultasUsuario : [];
  return cols.map(function (clave, idx) {
    return {
      idx: idx,
      clave: clave,
      visible: porPestana.indexOf(clave) === -1 && usuario.indexOf(idx) === -1,
      titulo: tituloColumna(pantalla, clave, tipo),
    };
  });
}

/** Índices que arrancan ocultos (columnDefs del DataTable): los de la pestaña más los de la persona. */
export function indicesOcultos(pantalla, tipo, ocultasUsuario) {
  return vistaColumnas(pantalla, tipo, ocultasUsuario).filter(function (c) { return !c.visible; })
    .map(function (c) { return c.idx; });
}

/** Columnas que exportan los botones de la tabla en esta pestaña (lo que ocultó la persona sí sale, como hoy). */
export function columnasExportables(pantalla, tipo) {
  const no = (propio(NO_EXPORTA, pantalla) || []).concat(ocultasDe(pantalla, tipo));
  return (propio(COLUMNAS, pantalla) || []).map(function (c, i) { return no.indexOf(c) === -1 ? i : -1; })
    .filter(function (i) { return i >= 0; });
}

/** El menú «Columnas» de la pestaña: solo las que la persona puede prender o apagar aquí ({índice: etiqueta}). */
export function menuColumnas(pantalla, tipo, toggleables) {
  const porPestana = ocultasDe(pantalla, tipo);
  const cols = propio(COLUMNAS, pantalla) || [];
  const out = {};
  Object.keys(toggleables || {}).forEach(function (k) {
    const i = Number(k);
    if (porPestana.indexOf(cols[i]) === -1) { out[i] = toggleables[k]; }
  });
  return out;
}

/**
 * Lista de índices que ocultó la persona, con `idx` puesto o quitado (ordenada, sin repetidos). El índice puede
 * llegar como texto (sale de un data-* del menú): se compara y se guarda siempre como número.
 */
export function alternarOculta(lista, idx, ocultar) {
  const i = Number(idx);
  const base = (Array.isArray(lista) ? lista : []).filter(function (n) { return n !== i; });
  if (ocultar && Number.isInteger(i)) { base.push(i); }
  return base.sort(function (a, b) { return a - b; });
}

/**
 * Índices guardados antes de la columna «Tipo»: de `desde` en adelante se recorren uno. Solo enteros de cero en
 * adelante y sin repetidos (queda el primero de cada uno, en su orden).
 */
export function migrarOcultas(lista, desde) {
  return (Array.isArray(lista) ? lista : [])
    .filter(function (n) { return Number.isInteger(n) && n >= 0; })
    .map(function (n) { return n >= desde ? n + 1 : n; })
    .filter(function (n, i, todos) { return todos.indexOf(n) === i; });
}

/**
 * Lo que enseña la barra de totales en la pestaña. En Todos no se suman importes de tipos distintos (un ingreso
 * más una nota de crédito más una nómina no es un total de nada); en Pagos el importe vive en la tabla; en
 * Traslados solo el Total.
 */
export function barraDe(tipo) {
  if (tipo === 'todos') {
    return { mostrar: ['registros'], nota: 'Los importes se suman dentro de cada tipo: elige una pestaña para verlos.' };
  }
  if (tipo === 'P') return { mostrar: ['registros'], nota: 'El monto de cada pago aparece en la tabla.' };
  if (tipo === 'T') return { mostrar: ['registros', 'total'], nota: '' };
  return { mostrar: ESTADISTICAS.slice(), nota: '' };
}

/** Importe de una fila: el monto de sus complementos si es un Pago (null si aún no se lee), si no su Total. */
export function importeFila(fila) {
  if (!fila) return null;
  if (fila.tipodecomprobante === 'P') {
    const m = fila.monto_pago;
    return (m === null || m === undefined || m === '' || !Number.isFinite(Number(m))) ? null : Number(m);
  }
  const n = Number(fila.monto_total);
  return (fila.monto_total === null || fila.monto_total === undefined || fila.monto_total === '' || !Number.isFinite(n)) ? null : n;
}

/**
 * Lo que enseña la celda sin importe: un Pago en varias monedas o uno cuyo complemento aún no se lee y, en una
 * fila que no es Pago, un comprobante que no trae su total.
 */
export function sinImporte(fila) {
  if (fila && fila.tipodecomprobante !== 'P') {
    return { texto: '—', titulo: 'Aún no tenemos el importe de este comprobante.' };
  }
  if (fila && fila.moneda_pago === 'varias') {
    return { texto: 'Varias monedas', titulo: 'Este complemento paga en varias monedas; el detalle está en su PDF.' };
  }
  return { texto: '—', titulo: 'El monto aparece en cuanto leamos este complemento de pago.' };
}

/**
 * HTML de la celda Total / Monto del pago / Neto pagado (la de un Pago lleva su moneda). Sin importe, la
 * explicación va en el title (mouse) y en un texto solo para lector de pantalla: en celular no hay mouse.
 */
export function celdaImporte(fila) {
  const v = importeFila(fila);
  if (v === null) {
    const s = sinImporte(fila);
    return '<span class="cell-cero" title="' + esc(s.titulo) + '">' + esc(s.texto) + '</span>'
      + '<span class="visually-hidden">' + esc(s.titulo) + '</span>';
  }
  const txt = dinero(v);
  return (fila.tipodecomprobante === 'P' && fila.moneda_pago) ? txt + ' <small class="text-muted">' + esc(fila.moneda_pago) + '</small>' : txt;
}

/**
 * Lo que dice celdaImporte, en texto plano: lo que llevan Excel, Copiar, Imprimir y PDF. Sin HTML y sin la
 * explicación para lector de pantalla, que en un archivo saldría pegada a la raya («—El monto aparece…»).
 */
export function textoImporte(fila) {
  const v = importeFila(fila);
  if (v === null) return sinImporte(fila).texto;
  const txt = dinero(v);
  return (fila.tipodecomprobante === 'P' && fila.moneda_pago) ? txt + ' ' + String(fila.moneda_pago) : txt;
}

/** Moneda del importe de la fila: la del pago en un Pago, la del comprobante en lo demás. */
export function monedaFila(fila) {
  if (!fila) return '';
  return fila.tipodecomprobante === 'P' ? String(fila.moneda_pago || '') : String(fila.moneda || '');
}

/** Recibidos: XXX es la moneda de los pagos; al salir de Pagos con XXX elegida, el filtro vuelve a MXN. */
export function monedaAlCambiar(nuevo, moneda) { return (moneda === 'XXX' && nuevo !== 'P') ? 'MXN' : moneda; }

/** Recibidos: en Pagos no se filtra por moneda (todos llevan XXX por regla del SAT). */
export function monedaAplica(tipo) { return tipo !== 'P'; }

export function tituloExport(pantalla, tipo) {
  const base = pantalla === 'recibidos' ? 'CFDIs Recibidos' : 'CFDIs Emitidos';
  return (tipo && tipo !== 'todos' && propio(NOMBRE, tipo)) ? base + ' · ' + NOMBRE[tipo] : base;
}

export function nombreArchivo(pantalla, tipo) {
  const base = pantalla === 'recibidos' ? 'CFDIs Recibidos' : 'CFDIs Emitidos';
  return (tipo && tipo !== 'todos' && propio(NOMBRE, tipo)) ? base + ' - ' + NOMBRE[tipo] : base;
}

/** Lo que dice el modal de un reporte de Reportes.php sobre lo que incluye. */
export function textoReporte(tipo) {
  if (!tipo || tipo === 'todos' || !propio(NOMBRE, tipo)) return 'Incluye todos los tipos de comprobante del mes.';
  return 'Incluye solo ' + (tipo === 'N' ? 'la Nómina' : 'los ' + NOMBRE[tipo]) + ' del mes, la pestaña que estás viendo.';
}

/** Insignia corta de la columna Tipo (I/E/T/P/N) con su nombre al pasar el mouse. */
export function insigniaTipo(tipo) {
  if (!propio(NOMBRE_TIPO, tipo)) return '<span class="fd-tipo">' + esc(tipo || '—') + '</span>';
  return '<span class="fd-tipo fd-tipo-' + tipo + '" title="' + NOMBRE_TIPO[tipo] + '">' + tipo + '</span>';
}
